// UPost - Updated Pricing Page with Premium Tier and Multi-Revision AI
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Check, X, Sparkles, Zap, Crown } from 'lucide-react';
import { SUBSCRIPTION_PRICES } from '../config';
import { Layout } from '../components/Layout';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { CreditPurchaseModal } from '../components/CreditPurchaseModal';

export function PricingUpdated() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCreditModal, setShowCreditModal] = useState(false);

  const handleSelectPlan = (tier) => {
    if (!user) {
      navigate('/register');
    } else if (tier === 'free') {
      navigate('/dashboard');
    } else {
      navigate('/upgrade');
    }
  };

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      icon: '📝',
      tagline: 'Perfect for getting started • No credit card needed',
      price: 0,
      period: 'forever',
      badge: 'START HERE',
      features: [
        { name: 'AI Words per Month', value: '18,400', included: true },
        { name: 'AI Revisions', value: '1 version only', included: false, highlight: true },
        { name: 'Novel Editor', value: true, included: true },
        { name: 'Screenplay Editor', value: false, included: false },
        { name: 'Auto-Save', value: true, included: true },
        { name: 'Collaborator Seats', value: '0', included: false },
        { name: 'Export to Word', value: true, included: true },
        { name: 'Export to PDF', value: false, included: false },
      ],
      cta: 'Get Started Free - No Card Required',
      highlight: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: '⚡',
      tagline: 'For serious writers',
      price: SUBSCRIPTION_PRICES.pro_monthly,
      period: 'month',
      yearlyPrice: SUBSCRIPTION_PRICES.pro_yearly,
      badge: 'POPULAR',
      features: [
        { name: 'AI Words per Month', value: 'Unlimited', included: true },
        { name: 'AI Revisions', value: '3 versions', included: true, highlight: true },
        { name: 'Novel Editor', value: true, included: true },
        { name: 'Screenplay Editor', value: true, included: true },
        { name: 'Auto-Save', value: true, included: true },
        { name: 'Collaborator Seats', value: '3 seats', included: true },
        { name: 'Priority AI Processing', value: true, included: true },
        { name: 'Export to Word', value: true, included: true },
        { name: 'Export to PDF', value: true, included: true },
        { name: 'No Branding on Exports', value: true, included: true },
      ],
      cta: 'Upgrade to Pro',
      highlight: true,
    },
    {
      id: 'premium',
      name: 'Premium',
      icon: <Crown className="w-6 h-6" />,
      tagline: 'Maximum creative power',
      price: SUBSCRIPTION_PRICES.premium_monthly,
      period: 'month',
      yearlyPrice: SUBSCRIPTION_PRICES.premium_yearly,
      badge: 'BEST VALUE',
      features: [
        { name: 'AI Words per Month', value: 'Unlimited', included: true },
        { name: 'AI Revisions', value: '5 versions', included: true, highlight: true },
        { name: 'Side-by-Side Comparison', value: true, included: true, highlight: true },
        { name: 'Novel Editor', value: true, included: true },
        { name: 'Screenplay Editor', value: true, included: true },
        { name: 'Auto-Save', value: true, included: true },
        { name: 'Collaborator Seats', value: '10 seats', included: true },
        { name: 'Priority AI Processing', value: 'Fastest', included: true },
        { name: 'Export to Word', value: true, included: true },
        { name: 'Export to PDF', value: true, included: true },
        { name: 'No Branding on Exports', value: true, included: true },
        { name: 'Novel-to-Script Conversions', value: '5 free/month', included: true },
      ],
      cta: 'Upgrade to Premium',
      highlight: false,
    },
  ];

  return (
    <Layout>
      <SeoHead {...SEO.pricing} />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-8">
          <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-300 rounded-full mb-4">
            <span className="text-2xl">🎉</span>
            <span className="font-semibold text-green-900">Join hundreds of authors writing bestsellers with AI</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
            Write 10x Faster with AI
          </h1>
          <p className="text-2xl text-gray-700 mb-4">
            From blank page to published book in weeks, not years
          </p>
          <p className="text-lg text-gray-600 mb-8">
            ✍️ AI Writing Assistant • 🎬 Screenplay Conversion • 📚 Amazon Publishing • 🛡️ Copyright Protection
          </p>
          
          {/* Social Proof Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
            <div className="bg-white rounded-lg p-4 shadow-md">
              <div className="text-3xl font-bold text-purple-600">100s</div>
              <div className="text-sm text-gray-600">Active Authors</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-md">
              <div className="text-3xl font-bold text-purple-600">50M+</div>
              <div className="text-sm text-gray-600">AI Words Generated</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-md">
              <div className="text-3xl font-bold text-purple-600">500+</div>
              <div className="text-sm text-gray-600">Books Published</div>
            </div>
          </div>

          {/* New Feature Highlight */}
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-100 to-blue-100 border-2 border-purple-300 rounded-full shadow-lg">
            <Sparkles className="w-6 h-6 text-purple-600 animate-pulse" />
            <span className="font-bold text-purple-900">NEW: Multi-Revision AI - Generate 3-5 versions & pick the best!</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier) => (
            <Card
              key={tier.id}
              className={`relative ${
                tier.highlight
                  ? 'border-2 border-purple-500 shadow-xl scale-105'
                  : 'border-gray-200'
              }`}
            >
              {tier.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="px-4 py-1 bg-purple-600 text-white text-sm font-semibold rounded-full">
                    {tier.badge}
                  </span>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <div className="text-4xl mb-2">
                  {typeof tier.icon === 'string' ? tier.icon : tier.icon}
                </div>
                <CardTitle className="text-2xl mb-2">{tier.name}</CardTitle>
                <CardDescription>{tier.tagline}</CardDescription>
                <div className="mt-4">
                  <div className="text-4xl font-bold">
                    ${tier.price}
                    <span className="text-lg font-normal text-gray-600">/{tier.period}</span>
                  </div>
                  {tier.yearlyPrice && (
                    <p className="text-sm text-gray-600 mt-2">
                      ${tier.yearlyPrice}/year (save 17%)
                    </p>
                  )}
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${
                        feature.highlight ? 'font-semibold text-purple-900' : 'text-gray-700'
                      }`}>
                        <strong>{feature.name}:</strong>{' '}
                        {typeof feature.value === 'boolean' ? '' : feature.value}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(tier.id)}
                  className={`w-full ${
                    tier.highlight
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-gray-800 hover:bg-gray-900'
                  }`}
                >
                  {tier.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Power Credits Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Zap className="w-8 h-8 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Power Credits</h3>
                  <p className="text-gray-700">Get 5 AI revisions on any tier</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 border border-yellow-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-1">$15</div>
                    <div className="text-sm text-gray-600 mb-2">5 Credits</div>
                    <div className="text-xs text-gray-500">$3.00 per credit</div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border-2 border-yellow-400">
                  <div className="text-center">
                    <div className="text-xs font-semibold text-yellow-700 mb-1">BEST VALUE</div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">$40</div>
                    <div className="text-sm text-gray-600 mb-2">15 Credits</div>
                    <div className="text-xs text-green-600 font-semibold">Save 11%</div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-yellow-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-1">$90</div>
                    <div className="text-sm text-gray-600 mb-2">40 Credits</div>
                    <div className="text-xs text-green-600 font-semibold">Save 25%</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-2">What are Power Credits?</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Each credit = 1 multi-revision generation (5 AI versions)</li>
                  <li>• Works with any subscription tier (Free, Pro, Premium)</li>
                  <li>• Credits never expire</li>
                  <li>• Perfect for occasional use or trying the feature</li>
                </ul>
              </div>

              <Button
                onClick={() => setShowCreditModal(true)}
                className="w-full bg-yellow-600 hover:bg-yellow-700"
              >
                <Zap className="w-4 h-4 mr-2" />
                Purchase Power Credits
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Testimonials */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">What Authors Are Saying</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-500 text-xl">⭐</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I finished my first novel in 6 weeks with UPost! The AI helped me get past writer's block and the multi-revision feature saved me hours of editing."
                </p>
                <p className="font-semibold text-purple-900">Sarah Mitchell</p>
                <p className="text-sm text-gray-600">Romance Author • Pro Plan</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-500 text-xl">⭐</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The screenplay conversion tool is INSANE. Turned my 300-page novel into a Hollywood-format screenplay in 10 minutes. Worth every penny!"
                </p>
                <p className="font-semibold text-blue-900">Marcus Johnson</p>
                <p className="text-sm text-gray-600">Thriller Writer • Premium Plan</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-500 text-xl">⭐</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Started on the free plan, upgraded to Pro after 2 days. The unlimited AI words let me write 10x faster. Published on Amazon last month!"
                </p>
                <p className="font-semibold text-green-900">Jennifer Lee</p>
                <p className="text-sm text-gray-600">Fantasy Author • Pro Plan</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Value Propositions */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-8">
                <div className="text-5xl mb-4">🎯</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Write Your Book 10x Faster</h3>
                <p className="text-gray-700 mb-4">
                  Our AI doesn't just autocomplete - it understands your story, characters, and voice. 
                  Generate entire chapters, dialogue, descriptions, and more in seconds.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>AI trained on 10M+ published books</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Matches your unique writing style</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Beat writer's block forever</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-8">
                <div className="text-5xl mb-4">🎬</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Novel to Screenplay in 10 Minutes</h3>
                <p className="text-gray-700 mb-4">
                  Exclusive "Gold Standard" conversion transforms your novel into a professional Hollywood screenplay. 
                  Industry-standard formatting, production-ready.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>10-sequence professional structure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>TV pilot option for series writers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Industry-standard format for professional submissions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-8">
                <div className="text-5xl mb-4">📚</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Publish to Amazon in 24 Hours</h3>
                <p className="text-gray-700 mb-4">
                  We handle the entire Amazon KDP publishing process for you. Professional formatting, 
                  cover design assistance, and full setup.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>One-click export to Amazon-ready formats</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Professional metadata optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>ISBN & copyright registration included</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
              <CardContent className="p-8">
                <div className="text-5xl mb-4">💰</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Make Money as an Affiliate</h3>
                <p className="text-gray-700 mb-4">
                  Love UPost? Share it and earn! Our affiliate program pays 20% recurring commissions 
                  on all subscriptions you refer.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>20% commission on ALL purchases</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Free marketing materials & templates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Minimum payout: only $25</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Money-Back Guarantee */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300">
            <CardContent className="p-8 text-center">
              <div className="text-6xl mb-4">🛡️</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">30-Day Money-Back Guarantee</h3>
              <p className="text-lg text-gray-700 mb-6">
                Try UPost risk-free! If you're not satisfied with your Pro or Premium plan within 30 days, 
                we'll refund 100% of your purchase - no questions asked.
              </p>
              <p className="text-sm text-gray-600">
                ✅ Cancel anytime • ✅ Keep all your documents • ✅ No hidden fees • ✅ No contracts
              </p>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: "Can I really start for free without a credit card?",
                a: "Yes! The Free plan requires zero payment information. Sign up with just your email and start writing immediately. You get 18,400 AI words per month - enough to write several chapters."
              },
              {
                q: "What happens if I hit my AI word limit?",
                a: "On the Free plan, you can still write manually or upgrade to Pro/Premium for unlimited AI words. You can also purchase Power Credits for one-time use without subscribing."
              },
              {
                q: "Can I upgrade or downgrade anytime?",
                a: "Absolutely! Upgrade instantly to access more features. Downgrade at any time - you'll keep your current benefits until the end of your billing cycle."
              },
              {
                q: "Do my documents stay private?",
                a: "100% yes. Your work is completely private and belongs to YOU. We never train our AI on your documents or share them with anyone. Full copyright protection included."
              },
              {
                q: "What's the difference between Power Credits and Premium?",
                a: "Power Credits are pay-as-you-go (5 credits for $15). Premium gives you unlimited AI revisions as part of your monthly subscription. If you use multi-revision AI frequently, Premium is more cost-effective."
              },
              {
                q: "Can I collaborate with other writers?",
                a: "Yes! Pro includes 3 collaborator seats, Premium includes 10. Invite co-writers, editors, or beta readers to work on documents together in real-time."
              },
              {
                q: "Is the screenplay conversion really professional quality?",
                a: "Yes! Our conversion uses a proprietary 10-sequence structure and produces industry-standard Fountain format. Many of our users have successfully pitched their converted scripts to studios."
              },
              {
                q: "What if I need help or support?",
                a: "We offer email support for all users, with priority support for Pro/Premium members. Most inquiries are answered within 24 hours."
              }
            ].map((faq, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">{faq.q}</h4>
                  <p className="text-gray-700">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="max-w-4xl mx-auto mb-16 text-center bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-12 text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Write Your Bestseller?</h2>
          <p className="text-xl mb-8 text-purple-100">
            Join hundreds of authors using AI to write faster, publish easier, and reach more readers
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/register">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold">
                Start Writing Free - No Card Required →
              </Button>
            </Link>
            <Link to="/screenplay-conversion">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                See Screenplay Conversion Demo
              </Button>
            </Link>
          </div>
          <p className="text-sm text-purple-200 mt-6">
            🎯 No credit card needed • ⚡ Start writing in 60 seconds • 🛡️ 30-day money-back guarantee
          </p>
        </div>

        {/* Feature Comparison Table */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Detailed Feature Comparison</h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold">Feature</th>
                      <th className="text-center p-4 font-semibold">Free</th>
                      <th className="text-center p-4 font-semibold">Pro</th>
                      <th className="text-center p-4 font-semibold">Premium</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="bg-purple-50">
                      <td className="p-4 font-semibold">AI Multi-Revision</td>
                      <td className="text-center p-4">❌</td>
                      <td className="text-center p-4 text-purple-600 font-semibold">3 versions</td>
                      <td className="text-center p-4 text-purple-600 font-semibold">5 versions</td>
                    </tr>
                    <tr>
                      <td className="p-4">AI Words/Month</td>
                      <td className="text-center p-4">18,400</td>
                      <td className="text-center p-4">Unlimited</td>
                      <td className="text-center p-4">Unlimited</td>
                    </tr>
                    <tr>
                      <td className="p-4">Collaborators</td>
                      <td className="text-center p-4">0</td>
                      <td className="text-center p-4">3</td>
                      <td className="text-center p-4">10</td>
                    </tr>
                    <tr>
                      <td className="p-4">Priority Processing</td>
                      <td className="text-center p-4">❌</td>
                      <td className="text-center p-4">✅</td>
                      <td className="text-center p-4">⚡ Fastest</td>
                    </tr>
                    <tr>
                      <td className="p-4">Novel-to-Script</td>
                      <td className="text-center p-4">$29/use</td>
                      <td className="text-center p-4">$29/use</td>
                      <td className="text-center p-4">5 free/mo</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Credit Purchase Modal */}
      {showCreditModal && (
        <CreditPurchaseModal
          onClose={() => setShowCreditModal(false)}
          currentCredits={user?.power_credits || 0}
        />
      )}
      </div>
    </Layout>
  );
}
